import { Fragment, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { AppVimeoGalleryVimeoLogoSmall } from '@elfsight/icons';
import { changeColorAlpha } from '@elfsight/lib-utils';
import { formatAbbrNumber, useBreakpoints } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import classNames from 'classnames';
import TextTruncate from 'react-text-truncate';
import {
  classnamePrefix,
  MOBILE_WIDTH_SMALL,
  TABLET_WIDTH
} from '../../../../constants';
import { dictionary } from '../../../dictionary';
import { Image } from '../Image';
import { Follow } from './Follow';

export const HeaderContent = ({ data, type }) => {
  const [finallyData, setFinallyData] = useState(data);
  const { breakpoints, headerElements, textColor, accentColor, sort, lang } =
    useSettings();
  const [containerBreakpoints, ref] = useBreakpoints(...breakpoints);
  const {
    name,
    description,
    bio,
    metadata: {
      connections: { users, videos, albums, followers, likes }
    },
    link,
    pictures: { sizes }
  } = finallyData;

  // TODO: Избавиться от этого костыля и получать информацию напрямую из data
  useEffect(() => {
    if (data) {
      setFinallyData(data);
    }
  }, [sort]);

  const counters = [
    {
      total: videos?.total ?? 0,
      title: dictionary[lang]?.['Videos'] || dictionary.en['Videos']
    },
    {
      total: users?.total ?? 0,
      title: dictionary[lang]?.['Users'] || dictionary.en['Users']
    },
    {
      total: followers?.total ?? 0,
      title: dictionary[lang]?.['Followers'] || dictionary.en['Followers']
    },
    {
      total: albums?.total ?? 0,
      title: dictionary[lang]?.['Albums'] || dictionary.en['Albums']
    },
    {
      total: likes?.total ?? 0,
      title: dictionary[lang]?.['Likes'] || dictionary.en['Likes']
    }
  ];

  const currentBreakpoint = Number(
    Object.keys(containerBreakpoints).filter(
      (item) => containerBreakpoints[item]
    )[0]
  );

  const onProfileLinkClick = () =>
    window.open(link, '_blank', 'noopener,noreferrer');

  return (
    <Container
      ref={ref}
      _currentBreakpoint={currentBreakpoint}
      _headerElements={headerElements?.length}
    >
      {type === 'user' && headerElements.includes('profilePicture') && (
        <PictureLink
          role="link"
          onClick={onProfileLinkClick}
          _currentBreakpoint={currentBreakpoint}
        >
          <Image imagesArray={sizes} alt={name} />
        </PictureLink>
      )}

      <Inner _currentBreakpoint={currentBreakpoint}>
        {headerElements.includes('name') && (
          <NameLink
            href={link}
            target="_blank"
            rel="nofollow noreferrer"
            _textColor={textColor}
            _currentBreakpoint={currentBreakpoint}
          >
            <NameInner>{name}</NameInner>

            {type !== 'user' && (
              <Type
                _accentColor={accentColor}
                _currentBreakpoint={currentBreakpoint}
              >
                <AppVimeoGalleryVimeoLogoSmall width="10" height="8" />

                {dictionary[lang]?.[`vimeo ${type}`] ||
                  dictionary.en[`vimeo ${type}`]}
              </Type>
            )}
          </NameLink>
        )}

        {currentBreakpoint === MOBILE_WIDTH_SMALL &&
          headerElements.includes('followButton') && (
            <Follow
              type={type}
              currentBreakpoint={currentBreakpoint}
              onClick={onProfileLinkClick}
            />
          )}

        {headerElements.includes('numbers') && (
          <Counters _currentBreakpoint={currentBreakpoint}>
            {counters.map((item, id) => (
              <Fragment key={id}>
                {item.total > 0 && (
                  <CountersItem
                    _textColor={textColor}
                    _currentBreakpoint={currentBreakpoint}
                  >
                    {formatAbbrNumber(item.total)} {item.title}
                  </CountersItem>
                )}
              </Fragment>
            ))}
          </Counters>
        )}

        {headerElements.includes('description') && (
          <Text _textColor={textColor} _currentBreakpoint={currentBreakpoint}>
            <TextTruncate
              line={2}
              truncateText="..."
              text={description || bio}
            />
          </Text>
        )}
      </Inner>

      {currentBreakpoint !== MOBILE_WIDTH_SMALL &&
        headerElements.includes('followButton') && (
          <Follow
            type={type}
            currentBreakpoint={currentBreakpoint}
            onClick={onProfileLinkClick}
          />
        )}
    </Container>
  );
};

const Container = styled.div.attrs(({ _currentBreakpoint }) => ({
  className: classNames(`${classnamePrefix}-header-content-container`, {
    [`${classnamePrefix}-header-content-container${_currentBreakpoint}`]:
      _currentBreakpoint
  })
}))`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 980px;
  width: 100%;
  margin: 0 auto 32px;

  ${({ _currentBreakpoint }) =>
    _currentBreakpoint === MOBILE_WIDTH_SMALL &&
    css`
      flex-direction: column;
      justify-content: center;
      text-align: center;
    `}

  ${({ _currentBreakpoint }) =>
    (_currentBreakpoint === TABLET_WIDTH ||
      _currentBreakpoint === MOBILE_WIDTH_SMALL) &&
    css`
      margin-bottom: 24px;
    `}

  ${({ _headerElements }) =>
    !_headerElements &&
    css`
      margin-bottom: 0;
    `}
`;

const Inner = styled.div.attrs({
  className: `${classnamePrefix}-header-content-inner`
})`
  margin-right: 32px;

  ${({ _currentBreakpoint }) =>
    _currentBreakpoint === MOBILE_WIDTH_SMALL &&
    css`
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
      align-items: center;
      margin-right: 0;
    `}
`;

const PictureLink = styled.div.attrs({
  className: `${classnamePrefix}-header-content-picture`
})`
  width: 88px;
  height: 88px;
  margin-right: 20px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  cursor: pointer;

  ${({ _currentBreakpoint }) =>
    _currentBreakpoint === MOBILE_WIDTH_SMALL &&
    css`
      margin-right: 0;
      margin-bottom: 12px;
    `}

  ${({ _currentBreakpoint }) =>
    _currentBreakpoint === TABLET_WIDTH &&
    css`
      margin-right: 12px;
    `}

    ${({ _currentBreakpoint }) =>
    (_currentBreakpoint === TABLET_WIDTH ||
      _currentBreakpoint === MOBILE_WIDTH_SMALL) &&
    css`
      width: 60px;
      height: 60px;
    `}


  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    border: solid 1px rgba(17, 17, 17, 0.1);
    border-radius: 50%;
  }
`;

const NameLink = styled.a.attrs({
  className: `${classnamePrefix}-header-content-name`
})`
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  line-height: 26px;
  text-decoration: none !important;
  margin-bottom: 4px;
  color: ${({ _textColor }) => _textColor || 'rgb(26, 46, 59)'};

  ${({ _currentBreakpoint }) =>
    (_currentBreakpoint === TABLET_WIDTH ||
      _currentBreakpoint === MOBILE_WIDTH_SMALL) &&
    css`
      font-size: 16px;
      line-height: 20px;
    `}
`;

const NameInner = styled.div.attrs({
  className: `${classnamePrefix}-header-content-nameInner`
})`
  display: inline-block;

  &:hover {
    text-decoration: underline;
  }
`;

const Type = styled.div.attrs({
  className: `${classnamePrefix}-header-content-type`
})`
  padding: 2px 4px;
  border-radius: 2px;
  text-transform: uppercase;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 8px;
  line-height: 11px;
  white-space: nowrap;
  text-decoration: none !important;
  transition: 0.3s ease background-color;
  color: ${({ _accentColor }) => _accentColor || '#03acef'};
  background-color: ${({ _accentColor }) =>
    _accentColor
      ? changeColorAlpha(_accentColor, -0.9)
      : 'rgba(3, 172, 239, 0.1)'};

  &:hover {
    background-color: ${({ _accentColor }) =>
      _accentColor ? changeColorAlpha(_accentColor, -0.75) : ''};
  }

  ${({ _currentBreakpoint }) =>
    _currentBreakpoint === MOBILE_WIDTH_SMALL &&
    css`
      display: none;
    `}

  & svg {
    display: inline-flex;
    margin-right: 4px;
    fill: ${({ _accentColor }) => _accentColor || '#03acef'};
  }
`;

const Counters = styled.div.attrs({
  className: `${classnamePrefix}-header-content-counters`
})`
  display: flex;
  flex-direction: row;
  margin: 4px -9px;

  ${({ _currentBreakpoint }) =>
    _currentBreakpoint === MOBILE_WIDTH_SMALL &&
    css`
      flex-wrap: wrap;
      justify-content: center;
    `}
`;

const CountersItem = styled.div.attrs({
  className: `${classnamePrefix}-header-content-countersItem`
})`
  font-size: 14px;
  line-height: 20px;
  margin: 0 9px;
  position: relative;
  white-space: nowrap;
  color: ${({ _textColor }) =>
    _textColor ? changeColorAlpha(_textColor, -0.5) : 'rgba(26, 46, 59, 0.5)'};

  ${({ _currentBreakpoint }) =>
    (_currentBreakpoint === MOBILE_WIDTH_SMALL ||
      _currentBreakpoint === TABLET_WIDTH) &&
    css`
      font-size: 12px;
      line-height: 16px;
    `}

  &:after {
    content: '';
    display: block;
    width: 2px;
    height: 2px;
    position: absolute;
    right: -11px;
    top: 50%;
    transform: translateX(-50%);
    background-color: ${({ _textColor }) =>
      _textColor
        ? changeColorAlpha(_textColor, -0.5)
        : 'rgba(26, 46, 59, 0.5)'};
  }

  &:last-child {
    margin-right: 0;

    &:after {
      display: none;
    }
  }
`;

const Text = styled.div.attrs({
  className: `${classnamePrefix}-header-content-text`
})`
  max-width: 600px;
  font-size: 14px;
  line-height: 20px;
  color: ${({ _textColor }) => _textColor || 'rgb(26, 46, 59)'};

  ${({ _currentBreakpoint }) =>
    (_currentBreakpoint === MOBILE_WIDTH_SMALL ||
      _currentBreakpoint === TABLET_WIDTH) &&
    css`
      font-size: 12px;
      line-height: 16px;
      max-height: 32px;
    `}

  & .${classnamePrefix}-clamp-lines__button {
    display: none;
  }
`;
